import React, { useEffect, useState } from 'react';
import ReactDOM from 'react-dom';
import { useSelector } from 'react-redux';
import { SELECTORS } from '@oneaudi/stck-store';
import { Headline, TextButton } from '@oneaudi/unified-web-components';
import {
  ThemeProvider,
  SYS_SPACE_RELATIVE_BP_LG_LG,
  SYS_SPACE_FIXED_300,
} from '@oneaudi/unified-web-common';
import { useI18n } from '@oneaudi/i18n-context';
import { ContentService } from '@oneaudi/content-service';
import { TrackingServiceV2 } from '@oneaudi/audi-tracking-service';
import {
  useServicesContext,
  CheckboxFilterGroup,
  CheckboxFilter,
  RangeFilter,
  FilterContext,
  FilterContextValue,
  ChipsList,
  formatCount,
  FilterOverlay,
  useDesktopOrMobileView,
  getActiveFiltersCount,
  DynamicGlobalStyles,
  useClientServerUtils,
} from '@oneaudi/vtp-shared';
import {
  QuickFiltersWrapper,
  QuickFiltersFooter,
  SelectedFiltersHeadline,
  StyledCloseButton,
  StyledAdvancedFiltersButton,
  RenderedQuickFiltersWrapper,
} from '../styles/QuickFilters.styles';
import { LocaleServiceV1 } from '@volkswagen-onehub/locale-service';
import { LayerHandleV1 } from '@volkswagen-onehub/gfa-layer-manager';
import { LayerManagerV25 } from '@volkswagen-onehub/layer-manager';
import { QuickLocationFilter } from './QuickLocationFilter';
import { OneCMSContent } from '../../../types/oneCMS';
import { SwitchNCUC } from './SwitchNCUC';
import { QuickModelFilter } from './QuickModelFilter';
import { trackOpenFilterOverlay } from '../../../tracking/quickFiltersTracking';
import { QuickFilterSkeleton } from '../../Tiles/Skeleton/QuickFilterSkeleton';

interface QuickFiltersProps {
  handleCloseQuickFiltersOverlay?: any;
  openMandatoryAreaSearchModal: () => void;
}

// eslint-disable-next-line no-empty-pattern
export const QuickFilters: React.FC<QuickFiltersProps> = ({
  handleCloseQuickFiltersOverlay,
  openMandatoryAreaSearchModal,
}) => {
  const allStockCarsHeadline = useI18n({
    id: `nemo.sc.filter.allStockCars.headline`,
    defaultMessage: 'All Stock Cars',
  });

  const selectedFilters = useI18n({
    id: `nemo.sc.filter.chosenFilters`,
    defaultMessage: 'Selected Filters',
  });

  const audiContentService = useServicesContext().getAdditionalService(
    'audi-content-service'
  ) as ContentService;
  const trackingService = useServicesContext().getAdditionalService(
    'audi-tracking-service'
  ) as TrackingServiceV2;
  const { quickFilters } = audiContentService.getContent().fields as OneCMSContent;
  const context = React.useContext(FilterContext);
  const localeService = useServicesContext().getAdditionalService(
    'gfa:locale-service'
  ) as LocaleServiceV1;
  const locale = `${localeService.language}-${localeService.countryCode}`;
  const totalResults = useSelector((state) => SELECTORS.VEHICLES.getTotalCountState(state));
  const formattedResultsCount = formatCount(totalResults, locale);
  const resultsButtonLabel = useI18n({
    id: `nemo.sc.plp.resultsButton`,
    defaultMessage: 'results',
  });
  const advancedButtonLabel = useI18n({
    id: `nemo.sc.plp.advancedButton`,
    defaultMessage: 'Advanced',
  });
  const isDesktop: boolean = useDesktopOrMobileView('1024px') === 'desktop';
  const { isClient } = useClientServerUtils();

  const {
    formattedCheckboxFilterDataFromSCS,
    filterData,
    configuredFilters,
    isFilterOverlayOpened,
    setIsFilterOverlayOpened,
    modelFilterData,
    newPickedContentSection,
    equipmentFilterProperlyFormatted,
  } = context as FilterContextValue;

  const layerManager = useServicesContext().getAdditionalService(
    'layer-manager'
  ) as LayerManagerV25;
  const [filterLayerNode, setFilterLayerNode] = useState<Element>();
  const [filterLayer, setFilterLayer] = useState<LayerHandleV1<{}>>();
  const focusLayerRef = React.useCallback(
    (node: any) => {
      setFilterLayerNode(node);
    },
    [setFilterLayerNode]
  );

  const openFilterOverlay = () => {
    const layer = layerManager.openFullscreenLayer(
      () => <div ref={focusLayerRef} />,
      {},
      {
        userCloseable: true,
        onClose: () => closeFilterOverlay(),
      }
    );
    setFilterLayer(layer);
    setIsFilterOverlayOpened(true);
    trackOpenFilterOverlay(trackingService, advancedButtonLabel);
  };

  const closeFilterOverlay = () => {
    filterLayer?.close();
    setIsFilterOverlayOpened(false);
  };
  const [configuredQuickFilters, setConfiguredQuickFilters] = useState<any[]>([]);
  const activeFiltersCount = getActiveFiltersCount();

  useEffect(() => {
    const quickFiltersArray: any = [];
    Object.values(quickFilters.fields).forEach((filterNameType) => {
      if (filterNameType !== '') {
        const lastIndex = filterNameType.lastIndexOf('.');
        const filterName = filterNameType.substring(0, lastIndex);
        const filterType = filterNameType.substring(lastIndex + 1);
        const filterObject = { filter: filterName, filterType };
        quickFiltersArray.push(filterObject);
      }
    });
    setConfiguredQuickFilters(quickFiltersArray);
  }, []);

  const renderQuickFilters = (configuredQuickFilter: any) => {
    if (
      configuredQuickFilter.filterType === 'checkbox' ||
      configuredQuickFilter.filterType === 'car-type-icon-checkbox' ||
      configuredQuickFilter.filterType === 'color-checkbox'
    ) {
      const checkboxGroup = formattedCheckboxFilterDataFromSCS?.find(
        (element: CheckboxFilterGroup) => element.groupHeadline === configuredQuickFilter.filter
      );

      if (checkboxGroup) {
        return (
          <CheckboxFilter
            key={`${checkboxGroup.groupHeadline}-checkboxfiltergroupheadline`}
            group={checkboxGroup}
            isCarTypeIconCheckbox={configuredQuickFilter.filterType === 'car-type-icon-checkbox'}
            isColorTypeCheckbox={configuredQuickFilter.filterType === 'color-checkbox'}
            isExtColorTypeCheckbox={
              configuredQuickFilter.filterType === 'color-checkbox' &&
              configuredQuickFilter.filter.startsWith('exterior')
            }
            isIntColorTypeCheckbox={
              configuredQuickFilter.filterType === 'color-checkbox' &&
              configuredQuickFilter.filter.startsWith('interior')
            }
            isPlpQuickFilters
            filterCategory={configuredQuickFilter.filter}
          />
        );
      }
      // eslint-disable-next-line no-console
      console.error(`No SCS data for configured ${configuredQuickFilter.filter} filter`);
    }

    if (configuredQuickFilter.filterType === 'model-checkbox') {
      return <QuickModelFilter filterCategory={configuredQuickFilter.filter} />;
    }

    if (configuredQuickFilter.filterType === 'range') {
      return (
        <RangeFilter
          filterGroup={configuredQuickFilter.filter}
          key={configuredQuickFilter.filter}
          isPlpQuickFilters
          filterCategory={configuredQuickFilter.filter}
        />
      );
    }

    if (configuredQuickFilter.filterType === 'location') {
      return (
        <QuickLocationFilter
          key={configuredQuickFilter.filter}
          openMandatoryAreaSearchModal={openMandatoryAreaSearchModal}
        />
      );
    }

    return null;
  };

  return (
    <>
      {!filterData || !formattedCheckboxFilterDataFromSCS || !isClient ? (
        <QuickFilterSkeleton />
      ) : (
        <QuickFiltersWrapper>
          {!isDesktop && (
            <StyledCloseButton
              variant="secondary"
              icon="cancel"
              onClick={() => handleCloseQuickFiltersOverlay()}
            />
          )}
          <Headline
            tag="h1"
            variant={{
              order: '3',
              type: 'headline',
              weight: 'normal',
            }}
            margin={[
              0,
              0,
              `${parseFloat(SYS_SPACE_RELATIVE_BP_LG_LG) + parseFloat(SYS_SPACE_FIXED_300)}px`,
              0,
            ]}
          >
            {allStockCarsHeadline}
          </Headline>
          {activeFiltersCount > 0 && (
            <SelectedFiltersHeadline variant={{ type: 'copy', order: '2', style: 'short' }}>
              {selectedFilters}
            </SelectedFiltersHeadline>
          )}
          <ChipsList isPlpQuickFilters />
          <SwitchNCUC />
          <RenderedQuickFiltersWrapper>
            {configuredQuickFilters.map((configuredQuickFilter) => {
              return renderQuickFilters(configuredQuickFilter);
            })}
          </RenderedQuickFiltersWrapper>
          <QuickFiltersFooter>
            <StyledAdvancedFiltersButton
              variant="secondary"
              icon="options"
              iconPosition="leading"
              onClick={() => openFilterOverlay()}
            >
              {advancedButtonLabel}
            </StyledAdvancedFiltersButton>
            {!isDesktop && (
              <TextButton
                variant="primary"
                onClick={() => handleCloseQuickFiltersOverlay()}
              >{`${formattedResultsCount} ${resultsButtonLabel}`}</TextButton>
            )}
          </QuickFiltersFooter>
        </QuickFiltersWrapper>
      )}
      {filterLayerNode &&
        ReactDOM.createPortal(
          <>
            <DynamicGlobalStyles />
            <ThemeProvider theme={{ name: 'dark' }}>
              <FilterOverlay
                data-audi-core-tracking-exclude
                data-audi-core-tracking-include="false"
                configuredFilters={configuredFilters}
                formattedCheckboxFilterDataFromSCS={formattedCheckboxFilterDataFromSCS}
                equipmentFilterCategories={equipmentFilterProperlyFormatted}
                isFilterOverlayOpened={isFilterOverlayOpened}
                modelFilterData={modelFilterData}
                newPickedContentSection={newPickedContentSection}
                closeOverlay={closeFilterOverlay}
              />
            </ThemeProvider>
          </>,
          filterLayerNode
        )}
    </>
  );
};
